import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import {
  isIE, stageLabel, stageTone, EDT_OPTIONS, STAGE_OPTIONS,
  LESSON_TYPE_OPTIONS_IE, LESSON_TYPE_OPTIONS_GB
} from '../lib/stage';

/**
 * GoStudents — the Students tab for Visionry Go.
 *
 * WHY it exists (Keelan's call): "current students" is a living list the
 * instructor works with weekly — Settings was the wrong home for it. This tab
 * mirrors Pro's Students tab in spirit, backed by `roster` instead of
 * `students` (Go holds no diary, so rows are audience + history, not bookings).
 *
 * Lifecycle: active ⇄ completed.
 *  - Accepted waitlist leads land here automatically (drop-5 promotion).
 *  - "Completed ✓" retires a student: kept for history, excluded from slot
 *    offers (API filters status='active'), claims stay trusted-tier.
 *  - Completing someone is a DEMAND EVENT — a finished student is a freed
 *    weekly slot — so the tab immediately nudges toward the waitlist.
 */

const API_BASE = 'https://visionry-api-production.up.railway.app';

const C = {
  bg: '#061618', card: '#0d3338', card2: '#10434a', border: '#10434a',
  teal: '#25a2b3', tealSoft: '#5fc4d2', text: '#e0f0f2', muted: '#7fb8c0',
  faint: '#3d5a5f', green: '#00cc88', greenBg: '#06231a',
  red: '#ff6b6b', redBg: '#2a0f0f', amber: '#ffb84d'
};

const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

const VEHICLE_LABEL = { instructor: "Instructor's car", student: 'Own car' };
const GEAR_LABEL = { manual: 'Manual', automatic: 'Automatic' };
const INSTR_PREF_LABEL = { male: 'Prefers male instructor', female: 'Prefers female instructor', any: 'Any instructor' };

// Same options + values as the WhatsApp signup questions (router.js
// WAITLIST_QUESTIONS) — this edit panel only ever offers what a student
// could actually have been asked, which is now country-dependent.
const AVAIL_OPTIONS = [
  ['weekday mornings', 'Weekday mornings'], ['weekday afternoons', 'Weekday afternoons'],
  ['weekday evenings', 'Weekday evenings'], ['weekends', 'Weekends'], ['flexible', 'Flexible / any']
];

// Drop 8 qualification chips — same conditional render-if-present pattern as
// the original area/EDT/availability chips.
function QualificationChips({ r }) {
  return (
    <>
      {r.learner_permit  && <Chip tone="plain">🪪 Permit: {r.learner_permit}</Chip>}
      {r.driver_number   && <Chip tone="plain">🆔 {r.driver_number}</Chip>}
      {r.exam_booked     && <Chip tone="plain">📝 Test booked: {r.exam_booked}</Chip>}
      {r.lesson_type     && <Chip tone="plain">📋 {r.lesson_type}</Chip>}
      {r.vehicle_pref    && <Chip tone="plain">🚗 {VEHICLE_LABEL[r.vehicle_pref] || r.vehicle_pref}</Chip>}
      {r.gear_pref       && <Chip tone="plain">⚙ {GEAR_LABEL[r.gear_pref] || r.gear_pref}</Chip>}
      {r.instructor_pref && <Chip tone="plain">👤 {INSTR_PREF_LABEL[r.instructor_pref] || r.instructor_pref}</Chip>}
    </>
  );
}

function Chip({ children, tone }) {
  const tones = {
    teal:  { bg: '#10434a', fg: '#5fc4d2' },
    amber: { bg: '#2a1f0a', fg: '#ffb84d' },
    plain: { bg: '#10434a', fg: '#7fb8c0' }
  };
  const t = tones[tone] || tones.plain;
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 6, fontSize: 11.5, fontWeight: 600, padding: '3px 8px', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

const selectStyle = {
  width: '100%', padding: '8px 10px', background: C.bg,
  border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
  fontSize: 14, outline: 'none', boxSizing: 'border-box'
};

function Field({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>{label}</label>
      <input
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={selectStyle}
      />
    </div>
  );
}

function SegBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, background: active ? C.teal : 'none',
      border: `1px solid ${active ? C.teal : C.border}`, borderRadius: 8,
      color: active ? C.bg : C.muted, fontSize: 13, fontWeight: active ? 600 : 400,
      padding: '10px', minHeight: 40, cursor: 'pointer'
    }}>{children}</button>
  );
}

function SegRow({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>{children}</div>
    </div>
  );
}

function SelectRow({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
        <option value="">—</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

// Editable panel for a roster row's qualification fields — same fields (and
// same conditional show/hide rules) as the WhatsApp signup flow, so a
// manually-added student (e.g. via Add students, never asked anything) can
// have their info backfilled, and nothing is offered here that wouldn't
// actually have been asked.
function QualificationEditPanel({ row, instructor, onSaved }) {
  const [form, setForm] = useState({
    area:            row.area || '',
    learner_permit:  row.learner_permit || '',
    driver_number:   row.driver_number || '',
    edt_progress:    row.edt_progress || '',
    lesson_stage:    row.lesson_stage || '',
    exam_booked:     row.exam_booked || '',
    lesson_type:     row.lesson_type || '',
    vehicle_pref:    row.vehicle_pref || '',
    gear_pref:       row.gear_pref || '',
    instructor_pref: row.instructor_pref || '',
    availability:    row.availability || '',
    avail_detail:    row.avail_detail || ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setMsg(null); }

  // Country decides which half of the qualification set applies. Mirrors the
  // `applicable` predicates in the API's WAITLIST_QUESTIONS — if the two ever
  // drift, this panel offers fields the student was never asked.
  const ie                 = isIE(instructor);
  // Driver number feeds the RSA EDT logbook; there is no UK equivalent.
  const showDriverNumber   = ie && form.learner_permit === 'yes';
  // In GB, 'test booked' is already one of the stage options, so a separate
  // exam question would be asking twice.
  const showExamBooked     = ie && !!form.edt_progress && form.edt_progress !== 'none';
  const atPretest          = ie
    ? form.edt_progress === 'complete'
    : ['test_booked', 'retaking'].includes(form.lesson_stage);
  const stageAnswered      = ie ? !!form.edt_progress : !!form.lesson_stage;
  const showLessonType     = stageAnswered && !atPretest;
  const gearOffered        = instructor.gear_types_offered || [];
  const showGearPref       = gearOffered.length !== 1;
  const staffGenders       = new Set((instructor.staff_members || []).map(s => s.gender));
  const showInstructorPref = staffGenders.has('male') && staffGenders.has('female');
  const showAvailDetail    = !!form.availability && !['weekends', 'flexible'].includes(form.availability);

  async function save() {
    setSaving(true);
    const { error } = await supabase
      .from('roster')
      .update({
        area:            form.area.trim() || null,
        learner_permit:  form.learner_permit || null,
        driver_number:   showDriverNumber ? (form.driver_number.trim() || null) : null,
        // Only ever write the column belonging to this instructor's market —
        // the other stays null, which is what stageLabel() reads off.
        edt_progress:    ie ? (form.edt_progress || null) : null,
        lesson_stage:    ie ? null : (form.lesson_stage || null),
        exam_booked:     showExamBooked ? (form.exam_booked || null) : null,
        lesson_type:     showLessonType ? (form.lesson_type || null) : null,
        vehicle_pref:    form.vehicle_pref || null,
        gear_pref:       showGearPref ? (form.gear_pref || null) : (gearOffered[0] || null),
        instructor_pref: showInstructorPref ? (form.instructor_pref || null) : null,
        availability:    form.availability || null,
        avail_detail:    showAvailDetail ? (form.avail_detail.trim() || null) : null
      })
      .eq('id', row.id);
    setSaving(false);
    if (error) return setMsg({ type: 'err', text: error.message });
    setMsg({ type: 'ok', text: 'Saved ✓' });
    onSaved();
  }

  return (
    <div style={{ marginTop: 12, padding: 16, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 12, color: C.teal, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
        Student details
      </div>

      <Field label="Area" value={form.area} onChange={v => set('area', v)} placeholder="e.g. Lucan" />

      <SegRow label="Holds a learner permit?">
        <SegBtn active={form.learner_permit === 'yes'} onClick={() => set('learner_permit', 'yes')}>Yes</SegBtn>
        <SegBtn active={form.learner_permit === 'no'}  onClick={() => set('learner_permit', 'no')}>No</SegBtn>
      </SegRow>

      {showDriverNumber && (
        <Field label="Driver number" value={form.driver_number} onChange={v => set('driver_number', v)} placeholder="From their learner permit" />
      )}

      {ie
        ? <SelectRow label="EDT progress" value={form.edt_progress} onChange={v => set('edt_progress', v)} options={EDT_OPTIONS} />
        : <SelectRow label="Stage" value={form.lesson_stage} onChange={v => set('lesson_stage', v)} options={STAGE_OPTIONS} />}

      {showExamBooked && (
        <SegRow label="Test date booked?">
          <SegBtn active={form.exam_booked === 'yes'} onClick={() => set('exam_booked', 'yes')}>Yes</SegBtn>
          <SegBtn active={form.exam_booked === 'no'}  onClick={() => set('exam_booked', 'no')}>No</SegBtn>
        </SegRow>
      )}

      {showLessonType && (
        <SelectRow label="Looking for" value={form.lesson_type} onChange={v => set('lesson_type', v)} options={ie ? LESSON_TYPE_OPTIONS_IE : LESSON_TYPE_OPTIONS_GB} />
      )}

      <SegRow label="Vehicle">
        <SegBtn active={form.vehicle_pref === 'instructor'} onClick={() => set('vehicle_pref', 'instructor')}>Instructor's car</SegBtn>
        <SegBtn active={form.vehicle_pref === 'student'}    onClick={() => set('vehicle_pref', 'student')}>Own car</SegBtn>
      </SegRow>

      {showGearPref && (
        <SegRow label="Gear">
          <SegBtn active={form.gear_pref === 'manual'}    onClick={() => set('gear_pref', 'manual')}>Manual</SegBtn>
          <SegBtn active={form.gear_pref === 'automatic'} onClick={() => set('gear_pref', 'automatic')}>Automatic</SegBtn>
        </SegRow>
      )}

      {showInstructorPref && (
        <SegRow label="Instructor preference">
          <SegBtn active={form.instructor_pref === 'male'}   onClick={() => set('instructor_pref', 'male')}>Male</SegBtn>
          <SegBtn active={form.instructor_pref === 'female'} onClick={() => set('instructor_pref', 'female')}>Female</SegBtn>
          <SegBtn active={form.instructor_pref === 'any'}    onClick={() => set('instructor_pref', 'any')}>Any</SegBtn>
        </SegRow>
      )}

      <SelectRow label="Availability" value={form.availability} onChange={v => set('availability', v)} options={AVAIL_OPTIONS} />

      {showAvailDetail && (
        <Field label="Day/time detail" value={form.avail_detail} onChange={v => set('avail_detail', v)} placeholder="e.g. Tuesdays after 5pm" />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <button onClick={save} disabled={saving} style={{
          background: C.teal, border: 'none', borderRadius: 8, color: C.bg,
          fontSize: 14, fontWeight: 600, padding: '11px 20px', minHeight: 44,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
        }}>
          {saving ? 'Saving…' : 'Save details'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.type === 'ok' ? C.green : C.red }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function sinceLabel(iso, prefix) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return `${prefix} today`;
  if (days < 7) return `${prefix} ${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  return `${prefix} ${weeks} week${weeks === 1 ? '' : 's'} ago`;
}

function Btn({ children, onClick, color = '#7fb8c0', border = '#10434a', bg, filled, disabled, fill, as, href }) {
  const style = {
    flex: fill ? 1 : 'none', background: filled ? bg : 'none',
    border: filled ? 'none' : `1px solid ${border}`, borderRadius: 10,
    color, fontSize: 13.5, fontWeight: 600, padding: '10px 14px', minHeight: 42,
    whiteSpace: 'nowrap', textAlign: 'center', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
  };
  if (as === 'a') return <a href={href} target="_blank" rel="noreferrer" style={style}>{children}</a>;
  return <button onClick={onClick} disabled={disabled} style={style}>{children}</button>;
}

// Add-students box — moved here from Settings, same /roster-bulk endpoint
// (normalisation stays server-side in one place).
function AddStudentsBox({ instructor, onAdded }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  async function upload() {
    if (!text.trim()) return;
    setBusy(true); setNote(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/roster-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ lines: text })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const skipped = body.skipped && body.skipped.length
          ? ` — ${body.skipped.length} line${body.skipped.length === 1 ? '' : 's'} skipped: ${body.skipped.slice(0, 3).join(' · ')}`
          : '';
        setNote({ type: 'ok', text: `${body.added} added ✅${skipped}` });
        setText('');
        onAdded && onAdded();
      } else {
        setNote({ type: 'err', text: body.error || 'Upload failed.' });
      }
    } catch {
      setNote({ type: 'err', text: 'Network error — try again.' });
    }
    setBusy(false);
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Add students</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        One per line: <i>Aoife Byrne, 0871234567</i> — they'll get first refusal on freed slots.
      </div>
      <textarea
        value={text} onChange={e => setText(e.target.value)} rows={3}
        placeholder={'Aoife Byrne, 0871234567\nSean Walsh, 0862345678'}
        style={{
          width: '100%', boxSizing: 'border-box', background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
          fontSize: 13.5, padding: '10px 12px', fontFamily: 'Poppins, sans-serif',
          resize: 'vertical', marginBottom: 8
        }}
      />
      {note && (
        <div style={{
          marginBottom: 8, padding: '9px 12px', borderRadius: 8, fontSize: 12.5,
          background: note.type === 'ok' ? C.greenBg : C.redBg,
          color: note.type === 'ok' ? C.green : C.red, wordBreak: 'break-word'
        }}>
          {note.text}
        </div>
      )}
      <Btn onClick={upload} disabled={busy || !text.trim()} filled bg={C.teal} color={C.bg}>
        {busy ? 'Adding…' : 'Add students'}
      </Btn>
    </div>
  );
}

export default function GoStudents({ instructor, onNavigate }) {
  const [rows, setRows]         = useState(null);
  const [view, setView]         = useState('active');   // 'active' | 'completed'
  const [busy, setBusy]         = useState(null);
  const [note, setNote]         = useState(null);
  const [expanded, setExpanded] = useState(null);   // id of the row showing its edit panel
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('roster')
      .select('id, name, phone, opted_out, status, created_at, completed_at, area, edt_progress, lesson_stage, availability, avail_detail, learner_permit, driver_number, exam_booked, lesson_type, vehicle_pref, gear_pref, instructor_pref')
      .eq('instructor_key', instructor.instructor_key)
      .order('created_at', { ascending: true });
    setRows(data || []);
  }, [instructor.instructor_key]);

  useEffect(() => { load(); }, [load]);

  const active    = (rows || []).filter(r => (r.status || 'active') === 'active');
  const completed = (rows || []).filter(r => r.status === 'completed');

  // Complete = retire + surface the freed capacity. The waiting-count query
  // runs on demand so the nudge always shows live numbers.
  async function markCompleted(row) {
    setBusy(row.id); setNote(null);
    const { error } = await supabase
      .from('roster')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('id', row.id)
      .eq('instructor_key', instructor.instructor_key);
    if (error) {
      setNote({ type: 'err', text: 'Could not update — try again.' });
    } else {
      const { count } = await supabase
        .from('waitlist').select('id', { count: 'exact', head: true })
        .eq('instructor_key', instructor.instructor_key).eq('status', 'waiting');
      const who = row.name ? row.name.split(' ')[0] : 'Student';
      setNote((count || 0) > 0
        ? { type: 'nudge', text: `${who} marked completed 🎉 That's a weekly slot freed — you have ${count} waiting.` }
        : { type: 'ok', text: `${who} marked completed 🎉` });
    }
    setBusy(null);
    await load();
  }

  async function reactivate(row) {
    setBusy(row.id); setNote(null);
    await supabase.from('roster')
      .update({ status: 'active', completed_at: null })
      .eq('id', row.id).eq('instructor_key', instructor.instructor_key);
    setNote({ type: 'ok', text: `${row.name ? row.name.split(' ')[0] : 'Student'} is active again.` });
    setBusy(null);
    await load();
  }

  async function remove(row) {
    setBusy(row.id); setNote(null);
    await supabase.from('roster').delete()
      .eq('id', row.id).eq('instructor_key', instructor.instructor_key);
    setBusy(null);
    await load();
  }

  if (!rows) return <div style={{ color: C.muted, padding: 20, fontFamily: 'Poppins, sans-serif' }}>Loading…</div>;

  const list = view === 'active' ? active : completed;
  const noteColors = {
    err:   { bg: C.redBg,   fg: C.red },
    ok:    { bg: C.greenBg, fg: C.green },
    nudge: { bg: C.card2,   fg: C.tealSoft }
  };

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
        Students
      </div>

      <AddStudentsBox instructor={instructor} onAdded={load} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['active', `Active (${active.length})`], ['completed', `Completed (${completed.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => { setView(id); setNote(null); }}
            style={{
              background: view === id ? C.card2 : 'none',
              border: `1px solid ${view === id ? C.teal : C.border}`,
              borderRadius: 10, color: view === id ? C.tealSoft : C.muted,
              fontSize: 13.5, fontWeight: 600, padding: '9px 16px', minHeight: 40, cursor: 'pointer'
            }}>
            {label}
          </button>
        ))}
      </div>

      {note && (
        <div style={{
          marginBottom: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: (noteColors[note.type] || noteColors.ok).bg,
          color: (noteColors[note.type] || noteColors.ok).fg,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
        }}>
          <span style={{ flex: 1, minWidth: 160 }}>{note.text}</span>
          {note.type === 'nudge' && (
            <Btn onClick={() => onNavigate && onNavigate('waitlist')} filled bg={C.teal} color={C.bg}>
              View waitlist →
            </Btn>
          )}
        </div>
      )}

      {list.length === 0 ? (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: 26, textAlign: 'center', color: C.muted, fontSize: 14
        }}>
          {view === 'active'
            ? <>No active students yet.<br /><span style={{ fontSize: 12, color: C.faint }}>Add them above, or accept someone from your waitlist — accepted students land here automatically.</span></>
            : <>No completed students yet.<br /><span style={{ fontSize: 12, color: C.faint }}>When someone passes their test or finishes up, mark them completed on the Active list.</span></>}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '4px 20px' }}>
          {list.map(r => {
            const isOpen = expanded === r.id;
            return (
            <div key={r.id} style={{ padding: '13px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center', gap: 12
              }}>
                <div
                  style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                >
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: C.muted, fontSize: 12 }}>{isOpen ? '▼' : '▸'}</span>
                    {r.name || '—'}
                    {r.opted_out && (
                      <span style={{ background: C.redBg, color: C.red, borderRadius: 6, fontSize: 10.5, fontWeight: 700, padding: '2px 7px' }}>
                        OPTED OUT
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    <span style={{ fontFamily: 'monospace' }}>{r.phone}</span>
                    {' · '}
                    {view === 'active' ? sinceLabel(r.created_at, 'added') : sinceLabel(r.completed_at, 'completed')}
                  </div>
                  {(r.area || r.edt_progress || r.lesson_stage || r.availability || r.learner_permit || r.exam_booked || r.lesson_type || r.vehicle_pref || r.gear_pref || r.instructor_pref) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {r.area         && <Chip tone="plain">📍 {r.area}</Chip>}
                      {stageLabel(r) && <Chip tone={stageTone(r)}>{stageLabel(r)}</Chip>}
                      {r.availability && <Chip tone="plain">🗓 {r.availability}{r.avail_detail ? ` (${r.avail_detail})` : ''}</Chip>}
                      <QualificationChips r={r} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <Btn as="a" href={`https://wa.me/${r.phone}`} color={C.muted} border={C.border} fill={isMobile}>💬</Btn>
                  {view === 'active' ? (
                    <Btn onClick={() => markCompleted(r)} disabled={busy === r.id} color={C.tealSoft} border={C.teal} fill={isMobile}>
                      {busy === r.id ? '…' : 'Completed ✓'}
                    </Btn>
                  ) : (
                    <Btn onClick={() => reactivate(r)} disabled={busy === r.id} color={C.tealSoft} border={C.teal} fill={isMobile}>
                      {busy === r.id ? '…' : 'Reactivate'}
                    </Btn>
                  )}
                  <Btn onClick={() => remove(r)} disabled={busy === r.id} color={C.red} border={C.redBg} fill={isMobile}>
                    Remove
                  </Btn>
                </div>
              </div>
              {isOpen && <QualificationEditPanel row={r} instructor={instructor} onSaved={load} />}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
